import { Box, Typography } from '@mui/material';
import type { ComponentProps } from 'react';
import { forwardRef, lazy, memo, Suspense, useRef } from 'react';
import Loading from '@/components/Loading';
import { combineSx } from '@/utils/combineSx';
import { useValuationsStore } from '../../store/valuationsStore';
import { AssetListHeader } from './AssetListHeader';
import { AssetListItem } from './AssetListItem';
import { EMPTY_ASSETS_MESSAGE } from './constants';

const emptyStyles = {
  height: '100%',
  width: '100%',
  display: 'flex',
  justifyContent: 'space-evenly',
  padding: '0.625rem',
  boxSizing: 'border-box',
};

const AssetListLoadMore = lazy(() =>
  import('./AssetListLoadMore').then(module => ({ default: module.AssetListLoadMore })),
);

const AssetListItems = () => {
  const sortedAssets = useValuationsStore.use.sortedAssets();
  const sortType = useValuationsStore.use.sortType();

  if (sortedAssets.length === 0) {
    return (
      <Typography sx={emptyStyles} data-id='asset-list-empty'>
        {EMPTY_ASSETS_MESSAGE}
      </Typography>
    );
  }

  return (
    <>
      {sortedAssets.map(asset => (
        <AssetListItem.Root key={asset.id} asset={asset}>
          <AssetListItem.Info asset={asset} />
          <AssetListItem.AssetListItemBadges asset={asset} sortType={sortType} />
        </AssetListItem.Root>
      ))}
    </>
  );
};

const AssetListContainer = forwardRef<HTMLDivElement, ComponentProps<typeof Box>>(
  ({ children, sx, ...props }, ref) => {
    const loadingAssets = useValuationsStore.use.loadingAssets();
    return (
      <Box
        ref={ref}
        overflow='auto'
        data-id='asset-list-container'
        sx={combineSx(
          {
            scrollBehavior: 'smooth',
            scrollSnapType: 'y proximity',
          },
          sx,
        )}
        {...props}
      >
        {loadingAssets ? <Loading /> : children}
      </Box>
    );
  },
);

AssetListContainer.displayName = 'AssetListContainer';

type AssetListProps = {
  onLoadMore: () => Promise<void> | void;
};

const AssetList = memo(({ onLoadMore }: AssetListProps) => {
  const listRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <AssetListHeader />
      <AssetListContainer ref={listRef}>
        <AssetListItems />
        <Suspense fallback={null}>
          <AssetListLoadMore rootRef={listRef} onLoadMore={onLoadMore} />
        </Suspense>
      </AssetListContainer>
    </>
  );
});

export { AssetList };
